import { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
} from '@/components/ui';
import { AdminRoleChips } from '@/features/admin/components/AdminRoleChips';
import { useAdminAccountsQuery } from '@/features/admin/hooks/useAdminAccountsQuery';
import type { AdminAccountSummary } from '@/features/admin/types/admin';
import type { RecipientAdmin } from '../types/businessInquiry';

interface RecipientPickerDialogProps {
  open: boolean;
  typeLabel: string;
  /** 이미 지정된 관리자 — 체크된 채 선택 불가. */
  assignedIds: ReadonlySet<string>;
  onClose: () => void;
  onAdd: (admins: RecipientAdmin[]) => void;
}

const toRecipient = (a: AdminAccountSummary): RecipientAdmin => ({
  adminUserId: a.adminUserId,
  name: a.name,
  loginId: a.loginId,
  email: a.email,
  active: a.status === 'ACTIVE',
  found: true,
});

/**
 * QA-628 BO-03 담당자 추가 레이어 — 관리자 계정 목록 API(GET /admin/v1/admins?keyword=)를 그대로 쓴다
 * (이름·이메일·로그인 ID 부분 일치). 검색어를 바꿔도 앞서 고른 사람은 유지, 이미 지정·비활성·이메일 없는 계정은 선택 불가.
 */
export const RecipientPickerDialog = ({ open, typeLabel, assignedIds, onClose, onAdd }: RecipientPickerDialogProps) => {
  const [keyword, setKeyword] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<AdminAccountSummary[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(keyword.trim()), 300);
    return () => clearTimeout(timer);
  }, [keyword]);

  useEffect(() => {
    if (!open) {
      setKeyword('');
      setDebounced('');
      setSelected([]);
    }
  }, [open]);

  const query = useAdminAccountsQuery({ keyword: debounced || undefined, page: 1, size: 50 });
  const rows = query.data?.items ?? [];
  const selectedIds = new Set(selected.map((a) => a.adminUserId));

  const blockReason = (a: AdminAccountSummary): string | null => {
    if (assignedIds.has(a.adminUserId)) return '지정됨';
    if (a.status !== 'ACTIVE') return '선택 불가';
    if (!a.email) return '이메일 미등록';
    return null;
  };

  const toggle = (a: AdminAccountSummary, checked: boolean) =>
    setSelected((prev) => (checked ? [...prev, a] : prev.filter((x) => x.adminUserId !== a.adminUserId)));

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-w-[640px]" data-testid="recipient-picker">
        <DialogHeader>
          <DialogTitle>수신 담당자 추가 — {typeLabel}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 text-[13px]">
          <div className="relative">
            <Search size={14} aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--content-muted)' }} />
            <Input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="이름, 이메일, 로그인 ID"
              aria-label="관리자 계정 검색"
              className="pl-8"
              data-testid="recipient-picker-search"
            />
          </div>
          <p style={{ color: 'var(--content-muted)' }}>
            관리자 계정 검색 결과 {query.data ? `${query.data.meta.totalItems.toLocaleString()}명` : ''} · 여러 명 선택 가능
          </p>
          <div className="max-h-[320px] overflow-y-auto rounded-md border" style={{ borderColor: 'var(--divider-strong)' }}>
            {query.isPending ? (
              <Skeleton className="m-3 h-20" />
            ) : query.isError ? (
              <p role="alert" className="p-3" style={{ color: 'var(--status-danger-fg)' }}>
                관리자 계정을 불러오지 못했습니다.
              </p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr style={{ background: 'var(--surface-1)' }}>
                    <th className="w-10 px-3 py-2" aria-label="선택" />
                    <th className="px-3 py-2 text-left font-semibold">이름</th>
                    <th className="px-3 py-2 text-left font-semibold">로그인 ID</th>
                    <th className="px-3 py-2 text-left font-semibold">역할</th>
                    <th className="px-3 py-2 text-left font-semibold">상태</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-4 text-center" style={{ color: 'var(--content-muted)' }}>
                        검색 결과가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    rows.map((a) => {
                      const reason = blockReason(a);
                      const assigned = assignedIds.has(a.adminUserId);
                      return (
                        <tr key={a.adminUserId} className="border-t" style={{ borderColor: 'var(--divider-default)' }} data-testid="recipient-picker-row">
                          <td className="px-3 py-2">
                            <Checkbox
                              checked={assigned || selectedIds.has(a.adminUserId)}
                              disabled={reason !== null}
                              onCheckedChange={(c) => toggle(a, c === true)}
                              aria-label={`${a.name} 선택`}
                            />
                          </td>
                          <td className="px-3 py-2">{a.name}</td>
                          <td className="px-3 py-2">{a.loginId}</td>
                          <td className="px-3 py-2">
                            <AdminRoleChips roles={a.roles} />
                          </td>
                          <td className="px-3 py-2">{a.status === 'ACTIVE' ? '활성' : '비활성'}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-[12px]" style={{ color: 'var(--content-muted)' }}>
                            {reason ?? ''}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
          <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
            이미 지정된 계정, 비활성 계정, 이메일 미등록 계정은 선택할 수 없습니다.
          </p>
          {selected.length > 0 ? (
            <div>
              <p className="mb-1 font-semibold">선택한 담당자</p>
              <div className="flex flex-wrap gap-1.5">
                {selected.map((a) => (
                  <span
                    key={a.adminUserId}
                    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5"
                    style={{ borderColor: 'var(--divider-strong)' }}
                  >
                    {a.name} <span style={{ color: 'var(--content-muted)' }}>{a.loginId}</span>
                    <button type="button" aria-label={`${a.name} 선택 취소`} onClick={() => toggle(a, false)}>
                      <X size={12} aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            취소
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={selected.length === 0}
            onClick={() => onAdd(selected.map(toRecipient))}
            data-testid="recipient-picker-add"
          >
            추가 ({selected.length}명)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
